import { useQuery } from "@tanstack/react-query";
import { Alert } from "antd";
import { createStyles, keyframes } from "antd-style";
import { MIME_TYPE_JSON } from "@rumble-it/common-lib/src/constants/mimeTypes";
import { ViewRenderer } from "./ViewRenderer";
import {
  useViewConfigurationAffiliateId,
  useViewConfigurationClient,
} from "./client";
import type { ViewConfiguration } from "./types";

interface ViewLoaderProps {
  dossierId: string;
  /**
   * The actor-specific route prefix used to fetch the view configuration,
   * e.g. "driver" (default, used by the `my` app) or "dealership" (used by `portal`).
   */
  actorPrefix?: string;
}

const spin = keyframes`
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
`;

const useStyles = createStyles(() => ({
  loaderContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    height: "100%",
  },
  logo: {
    width: "70px !important",
    animation: `${spin} 2s linear infinite`,
  },
}));

export const ViewLoader = ({
  dossierId,
  actorPrefix = "driver",
}: ViewLoaderProps) => {
  const { styles } = useStyles();
  const axiosInstance = useViewConfigurationClient();
  const affiliateId = useViewConfigurationAffiliateId();

  const viewQuery = useQuery({
    queryKey: ["view-configuration", affiliateId, actorPrefix, dossierId],
    queryFn: () =>
      axiosInstance
        .get<ViewConfiguration>(`/${actorPrefix}/dossiers/${dossierId}/serve`)
        .then((response) => response.data),
  });

  const themeId = viewQuery?.data?.themeId;

  const themeQuery = useQuery({
    queryKey: ["formTheme", affiliateId, themeId],
    queryFn: async () =>
      axiosInstance
        .get<{ cssVariables: Record<string, string | number> }>(
          `/themes/${themeId}/serve`,
          {
            headers: {
              Accept: MIME_TYPE_JSON,
              "Content-Type": MIME_TYPE_JSON,
            },
          },
        )
        .then((response) => response.data),
    enabled: !!themeId,
  });

  const cdnDomain = import.meta.env.VITE_APP_CDN_DOMAIN;

  if (viewQuery.isLoading || themeQuery.isLoading) {
    return (
      <div className={styles.loaderContainer}>
        <img
          src={`https://${cdnDomain}/static/assets/rumble/rumble-logo-circle.svg`}
          alt="RumbleDirect logo"
          className={styles.logo}
        />
      </div>
    );
  }

  if (viewQuery.isError || !viewQuery.data) {
    return (
      <Alert
        type="error"
        description="Er is een fout opgetreden bij het laden van de dossiergegevens. Probeer het later opnieuw."
      />
    );
  }

  if (themeId && (themeQuery.isError || !themeQuery.data)) {
    return (
      <Alert
        type="error"
        description="Er is een fout opgetreden bij het laden van de dossiergegevens. Probeer het later opnieuw."
      />
    );
  }

  return <ViewRenderer theme={themeQuery.data} view={viewQuery.data} />;
};
